import { useCallback, useEffect, useRef, useState, type ChangeEvent, type MouseEvent, type SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import BorderlessTextAreaOnly from 'components/Common/Form/Input/BorderlessTextAreaOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import SolidArrowUpIcon from 'LuxKit/Icons/solid/SolidArrowUpIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { TRIP_LIST_NOTE_MAX_LENGTH, normalizeTripListNote } from '../../utils/tripListNoteUtils'

const NoteShell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(6)};
  width: 100%;
  min-height: ${rem(32)};
  padding: ${rem(6)} ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: text;

  &.is-placeholder {
    align-items: center;
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.is-focused {
    border-color: ${props => props.theme.palette.neutral.surface.normalForeground};
  }

  &.is-disabled {
    cursor: default;
    opacity: 0.6;
  }
`

const NoteBody = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`

const EditorRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
`

const NoteTextArea = styled(BorderlessTextAreaOnly)`
  flex: 1;
  min-width: 0;
  max-height: ${rem(48)};
  overflow: hidden;

  &::after {
    max-height: ${rem(48)};
    padding: 0;
    overflow: hidden;
  }

  & > textarea {
    min-height: ${rem(48)};
    max-height: ${rem(48)};
    padding: 0;
    font-size: 12px;
    line-height: 16px;
    overflow: auto;
  }
`

const SaveButton = styled(IconButton)`
  flex-shrink: 0;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

interface Props {
  note: string | undefined
  title: string
  disabled: boolean
  saving: boolean
  onChange: (notes: string | undefined) => void
}

function TripListRowNote({ note, title, disabled, saving, onChange }: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note ?? '')

  useEffect(() => {
    setDraft(note ?? '')
  }, [note])

  const placeholder = intl.formatMessage({
    id: 'cnVq7DmC07wNFmbnUgrs',
    defaultMessage: 'Add note',
    description: 'Placeholder text for empty notes cells in the TripChat itinerary list view',
  })

  const ariaLabel = intl.formatMessage(
    {
      id: 'dfoFEtIRwk_vSVLMPpUd',
      defaultMessage: 'Note for {title}',
      description: 'Accessible label for the editable note field in the TripChat itinerary list view',
    },
    { title },
  )

  const saveLabel = intl.formatMessage({
    id: 'JPZcDRlVNm-X_6WcjQDB',
    defaultMessage: 'Save note',
    description: 'Accessible label for the button that saves a TripChat itinerary list note',
  })

  const stopControlEvent = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const startEditing = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (disabled) return
    setEditing(true)
  }, [disabled])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(event.currentTarget.value)
  }, [])

  const save = useCallback(() => {
    const nextNote = normalizeTripListNote(draft)
    if (nextNote !== note) {
      onChange(nextNote)
    }
  }, [draft, note, onChange])

  const handleBlur = useCallback(() => {
    setEditing(false)
    save()
  }, [save])

  const handleSavePointerDown = useCallback((event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    inputRef.current?.blur()
  }, [])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  if (editing) {
    return (
      <NoteShell
        className={clsx('is-focused', disabled && 'is-disabled')}
        onClick={stopControlEvent}
        onMouseDown={stopControlEvent}
      >
        <EditorRow>
          <NoteTextArea
            ref={inputRef}
            value={draft}
            onChange={handleInputChange}
            onBlur={handleBlur}
            maxLength={TRIP_LIST_NOTE_MAX_LENGTH}
            aria-label={ariaLabel}
            placeholder={placeholder}
            disabled={disabled || saving}
          />
          <SaveButton
            shape="square"
            kind="tertiary"
            variant="default"
            size="small"
            aria-label={saveLabel}
            type="button"
            onPointerDown={handleSavePointerDown}
            onMouseDown={handleSavePointerDown}
            onTouchStart={handleSavePointerDown}
            disabled={disabled || saving}
          >
            {saving ? <SpinnerIcon size="S" /> : <SolidArrowUpIcon size={20} />}
          </SaveButton>
        </EditorRow>
      </NoteShell>
    )
  }

  return (
    <NoteShell
      className={clsx(!note && 'is-placeholder', disabled && 'is-disabled')}
      onClick={startEditing}
      onMouseDown={stopControlEvent}
    >
      {!note && <LinePenIcon size="XS" />}
      <NoteBody>
        <BodyText
          variant="small"
          colour={note ? 'normal-contrast' : 'light-foreground-alt'}
          lineClamp={3}
          wrap="pre-wrap"
        >
          {note ?? placeholder}
        </BodyText>
      </NoteBody>
    </NoteShell>
  )
}

export default TripListRowNote
